import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FaCube, FaExternalLinkAlt, FaBuilding, FaChevronRight,
  FaTh, FaLayerGroup, FaPlay, FaArrowRight
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import './Tours.css';

// Resolve a thumbnail URL. Accepts absolute URLs, blob URLs, or relative
// server-side paths (e.g. "uploads/foo.png" → full API URL).
const getImageUrl = (url) => {
  if (!url) return null;
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const clean = url.startsWith('/') ? url.slice(1) : url;
  return getApiEndpoint(`/${clean}`);
};

export default function Tours() {
  const { property, id } = useOutletContext();
  const [floors, setFloors] = useState([]);
  const [activeFloor, setActiveFloor] = useState('all');
  const [loading, setLoading] = useState(true);
  const [thumbFailed, setThumbFailed] = useState({}); // link.id → true if preview failed

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint(`/api/properties/${id}/tours`), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setFloors(list);
        setActiveFloor('all');
      })
      .catch(() => setFloors([]))
      .finally(() => setLoading(false));
  }, [id]);

  const activeFloorData = floors.find(f => f.id === activeFloor);

  const { totalTourCount, floorsWithTours } = useMemo(() => {
    let total = 0;
    let withTours = 0;
    floors.forEach(f => {
      const n = (f.links || []).length;
      total += n;
      if (n > 0) withTours += 1;
    });
    return { totalTourCount: total, floorsWithTours: withTours };
  }, [floors]);

  const floorsOrdered = useMemo(
    () => [...floors].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.floor_number ?? 0) - (b.floor_number ?? 0)),
    [floors]
  );

  const renderTourCard = (link, floor, idx, { showFloorBadge }) => {
    let domain = link.url;
    try { domain = new URL(link.url).hostname.replace(/^www\./, ''); } catch { /* keep raw */ }
    const title = (link.title && String(link.title).trim()) || `3D Tour ${idx + 1}`;
    const thumb = getImageUrl(link.thumbnail_url);

    return (
      <a
        key={`${floor.id}-${link.id}`}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="tours-card"
      >
        <div className="tours-card-thumb">
          {thumb ? (
            <img src={thumb} alt={title} className="tours-card-thumb-img" />
          ) : (
            <div className="tours-card-thumb-placeholder">
              <FaCube />
            </div>
          )}
          <div className="tours-card-overlay">
            <div className="tours-card-overlay-btn">
              <FaPlay /> Launch Tour
            </div>
          </div>
          {showFloorBadge && (
            <div className="tours-card-floor-pill">
              <FaLayerGroup aria-hidden />
              <span>{floor.floor_number != null ? `F${floor.floor_number}` : '—'} · {floor.floor_name}</span>
            </div>
          )}
        </div>
        <div className="tours-card-body">
          <div className="tours-card-body-left">
            <h3 className="tours-card-title">{title}</h3>
            <p className="tours-card-desc">{domain}</p>
          </div>
          <div className="tours-card-arrow">
            <FaExternalLinkAlt />
          </div>
        </div>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="tours-page">
        <div className="tours-loading">
          <div className="tours-loading-spinner" />
          <span>Loading 3D Tours…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tours-page">
      {floors.length === 0 ? (
        <>
          <div className="tours-header">
            <div className="tours-header-icon"><FaCube /></div>
            <div>
              <h1 className="tours-title">3D Tours</h1>
              <p className="tours-subtitle">{property?.name || 'Property'} · Immersive virtual walkthroughs</p>
            </div>
          </div>
          <div className="tours-empty">
            <FaBuilding className="tours-empty-icon" />
            <h3>No 3D Tours Available</h3>
            <p>Tours for this property haven't been added yet. Contact CloudSafe staff to set them up.</p>
          </div>
        </>
      ) : (
        <div className="tours-layout">
          {/* ── Floor sidebar ── */}
          <aside className="tours-floor-sidebar" aria-label="Floor navigation">
            <div className="tours-floor-sidebar-header">
              <FaCube className="tours-sidebar-logo-icon" />
              <span>3D Tours</span>
            </div>

            <div className="tours-floor-sidebar-label">View</div>
            <button
              type="button"
              className={`tours-floor-btn tours-floor-btn-all ${activeFloor === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFloor('all')}
            >
              <span className="tours-floor-number tours-floor-number-wide"><FaTh /></span>
              <span className="tours-floor-name">All Scans</span>
              <span className="tours-floor-count">{totalTourCount}</span>
            </button>

            <div className="tours-floor-sidebar-label tours-floor-sidebar-label-mt">Floors</div>
            {floorsOrdered.map(floor => {
              const count = (floor.links || []).length;
              return (
                <button
                  key={floor.id}
                  type="button"
                  className={`tours-floor-btn ${activeFloor === floor.id ? 'active' : ''} ${count === 0 ? 'empty' : ''}`}
                  onClick={() => setActiveFloor(floor.id)}
                >
                  <span className="tours-floor-number">
                    {floor.floor_number != null ? `F${floor.floor_number}` : '—'}
                  </span>
                  <span className="tours-floor-name">{floor.floor_name}</span>
                  {count > 0
                    ? <span className="tours-floor-count">{count}</span>
                    : <span className="tours-floor-count tours-floor-count-empty">0</span>
                  }
                </button>
              );
            })}
          </aside>

          {/* ── Main content ── */}
          <div className="tours-content">
            {activeFloor === 'all' ? (
              <>
                {/* Compact dashboard banner */}
                <div className="tours-dashboard-banner">
                  <div className="tours-dashboard-banner-left">
                    <p className="tours-dashboard-kicker"><FaBuilding /> Building Hub</p>
                    <h2 className="tours-dashboard-banner-title">{property?.name || 'Property'}</h2>
                    <p className="tours-dashboard-banner-sub">All immersive walkthroughs across every floor in one view</p>
                  </div>
                  <div className="tours-dashboard-stats">
                    <div className="tours-stat-card">
                      <span className="tours-stat-value">{totalTourCount}</span>
                      <span className="tours-stat-label">Total scans</span>
                    </div>
                    <div className="tours-stat-card">
                      <span className="tours-stat-value">{floorsWithTours}</span>
                      <span className="tours-stat-label">Floors covered</span>
                    </div>
                    <div className="tours-stat-card">
                      <span className="tours-stat-value">{floors.length}</span>
                      <span className="tours-stat-label">Floors total</span>
                    </div>
                  </div>
                </div>

                {totalTourCount === 0 ? (
                  <div className="tours-floor-empty tours-floor-empty-spaced">
                    <FaCube className="tours-floor-empty-icon" />
                    <p>No tour links have been added yet. CloudSafe staff can add scans per floor.</p>
                  </div>
                ) : (
                  <div className="tours-overview-sections">
                    {floorsOrdered.map(floor => {
                      const links = floor.links || [];
                      if (links.length === 0) return null;
                      return (
                        <section key={floor.id} className="tours-floor-section">
                          <div className="tours-floor-section-head">
                            <button
                              className="tours-floor-section-title-btn"
                              onClick={() => setActiveFloor(floor.id)}
                            >
                              <span className="tours-floor-section-badge">
                                {floor.floor_number != null ? `F${floor.floor_number}` : '—'}
                              </span>
                              <span className="tours-floor-section-name">{floor.floor_name}</span>
                              <FaChevronRight className="tours-floor-section-chevron" />
                            </button>
                            <span className="tours-floor-section-count">
                              {links.length} scan{links.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="tours-grid">
                            {links.map((link, idx) =>
                              renderTourCard(link, floor, idx, { showFloorBadge: false })
                            )}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </>
            ) : activeFloorData ? (
              <>
                <div className="tours-content-header">
                  <div className="tours-content-header-left">
                    <span className="tours-content-floor-badge">
                      {activeFloorData.floor_number != null ? `F${activeFloorData.floor_number}` : '—'}
                    </span>
                    <h2 className="tours-content-title">{activeFloorData.floor_name}</h2>
                  </div>
                  <span className="tours-content-count">
                    {(activeFloorData.links || []).length} tour{(activeFloorData.links || []).length !== 1 ? 's' : ''}
                  </span>
                </div>

                {(activeFloorData.links || []).length === 0 ? (
                  <div className="tours-floor-empty">
                    <FaCube className="tours-floor-empty-icon" />
                    <p>No tours added for this floor yet.</p>
                  </div>
                ) : (
                  <div className="tours-grid">
                    {(activeFloorData.links || []).map((link, idx) => {
                      let domain = link.url;
                      try { domain = new URL(link.url).hostname.replace(/^www\./, ''); } catch {}
                      return (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tours-card"
                        >
                          <div className="tours-card-thumb">
                            <div className="tours-card-thumb-placeholder">
                              <FaCube />
                            </div>
                            <div className="tours-card-overlay">
                              <div className="tours-card-overlay-btn">
                                <FaExternalLinkAlt />
                                Launch Tour
                              </div>
                            </div>
                          </div>
                          <div className="tours-card-body">
                            <div className="tours-card-body-left">
                              <h3 className="tours-card-title">3D Tour {idx + 1}</h3>
                              <p className="tours-card-desc">{domain}</p>
                            </div>
                            <div className="tours-card-arrow">
                              <FaArrowRight />
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
